import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
// The sdc design-system token contract + the panel.css the reused message-preview
// parts still rely on — loaded once at app entry.
import './styles/sdc-tokens.css';
import './styles/panel.css';

const container = document.getElementById('root');
if (!container) throw new Error('Missing #root element');

// The redesigned experience (Run / Edit / Presenter / Manage on the sdc design
// system) is now the DEFAULT — no query flag needed. Escape hatches:
//   ?legacy      — the original SLDS control panel (kept until it is retired)
//   ?ui-harness  — the sdc primitives gallery (dev only, Ticket 001)
//   ?edit        — the redesigned shell mounted straight into Edit Mode
// `?run-shell` still resolves to the default, so old links keep working.
const params = new URLSearchParams(window.location.search);
const legacy = params.has('legacy');
const harness = import.meta.env.DEV && params.has('ui-harness');
// ?composer — MessageComposer examples (editing + collapsed states). Stands in for
// Storybook, which this prototype branch doesn't have. VITE_START_VIEW=composer
// opens it directly in the single-file build.
const composerExamples = params.has('composer') || import.meta.env.VITE_START_VIEW === 'composer';
// VITE_START_VIEW=edit is used by the single-file prototype build (no query string on file://).
const editEntry = params.has('edit') || import.meta.env.VITE_START_VIEW === 'edit';

if (legacy) {
  // `slds-scope` on the body activates the Salesforce Lightning Design System
  // styles the original panel's CSS builds on.
  document.body.classList.add('slds-scope');
  import('./App').then(({ App }) => {
    createRoot(container).render(
      <StrictMode>
        <App />
      </StrictMode>,
    );
  });
} else if (composerExamples) {
  import('./components/edit/timeline/MessageComposer.examples').then(({ MessageComposerExamples }) => {
    createRoot(container).render(
      <StrictMode>
        <MessageComposerExamples />
      </StrictMode>,
    );
  });
} else if (harness) {
  import('./components/ui/UiHarness').then(({ UiHarness }) => {
    createRoot(container).render(
      <StrictMode>
        <UiHarness />
      </StrictMode>,
    );
  });
} else {
  // `?edit` is a convenience entry: it mounts the same RunScreen host and flips
  // the shell's view-state to Edit before first paint, so Edit Mode is reachable
  // directly as well as from the rail nav.
  Promise.all([
    import('./components/run/RunScreen'),
    import('./stores/shellStore'),
  ]).then(([{ RunScreen }, { useShellStore }]) => {
    if (editEntry) useShellStore.setState({ view: 'edit' });
    createRoot(container).render(
      <StrictMode>
        <RunScreen />
      </StrictMode>,
    );
  });
}
